import { FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { resolveMailLanguage } from "../../emailLanguage";
import {
  CONTACT_EMAIL,
  IMPRINT_URL,
  PRIVACY_URL,
  TERMS_URL,
} from "../../externalLinks";
import { withEntryQuery } from "../../entryParams";
import { formatDate } from "../../format";
import { ApiFailure } from "../../restClient";
import {
  ANONYMOUS_CATEGORY,
  DSA_CATEGORIES,
  DsaCategory,
  DsaContactPoint,
  DsaObjectionFormValues,
  DsaReportFormValues,
  DsaReportResponse,
  fetchContactPoint,
  submitDsaReport,
  submitObjection,
} from "./dsaApi";
import "../public-page.css";

/** Field names that can carry a validation message. */
type FieldError =
  | "contentReference"
  | "description"
  | "reporterName"
  | "reporterEmail"
  | "goodFaithConfirmed";

const EMPTY_OBJECTION: DsaObjectionFormValues = {
  caseId: "",
  email: "",
  statement: "",
};

const EMPTY_FORM: DsaReportFormValues = {
  category: "IllegalContent",
  contentReference: "",
  description: "",
  reporterName: "",
  reporterEmail: "",
  goodFaithConfirmed: false,
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Mirror the column lengths on DsaReport. Without them an over-long entry comes
// back as model validation, whose ValidationProblemDetails carries no `code`, so
// the page can only show the generic banner and never names the field at fault.
const MAX_CONTENT_REFERENCE = 2000;
const MAX_DESCRIPTION = 8000;
const MAX_REPORTER_NAME = 200;
const MAX_REPORTER_EMAIL = 320;

/**
 * The Art. 16 DSA notice form and the point of contact. It lives here and not in
 * the Flutter app because Art. 16 (1) wants the reporting route usable by anyone,
 * and everything in the app sits behind the login.
 *
 * The fields are what Art. 16 (2) makes a notice out of, with one exception built
 * in: for material depicting the sexual abuse of children Art. 16 (2) does not
 * require contact details, so those two fields become optional.
 */
function DsaReportPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const locale = i18n.language || "de";
  const language = resolveMailLanguage(locale);

  const [values, setValues] = useState<DsaReportFormValues>(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState<FieldError[]>([]);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const [receipt, setReceipt] = useState<DsaReportResponse | null>(null);
  const [contact, setContact] = useState<DsaContactPoint | null>(null);
  const [objection, setObjection] =
    useState<DsaObjectionFormValues>(EMPTY_OBJECTION);
  const [objectionErrors, setObjectionErrors] = useState<string[]>([]);
  const [objectionBusy, setObjectionBusy] = useState(false);
  const [objectionFailure, setObjectionFailure] = useState<ApiFailure | null>(
    null,
  );
  const [objectionDone, setObjectionDone] = useState(false);

  // Read once, with fallback addresses when the call fails: Art. 12 (2) DSA wants
  // the contact reachable, not conditional on our API.
  useEffect(() => {
    let cancelled = false;

    fetchContactPoint().then((result) => {
      if (!cancelled && result.ok) {
        setContact(result.data);
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const contactOptional = values.category === ANONYMOUS_CATEGORY;

  const setValue = <K extends keyof DsaReportFormValues>(
    key: K,
    value: DsaReportFormValues[K],
  ) => setValues((current) => ({ ...current, [key]: value }));

  const collectErrors = (): FieldError[] => {
    const errors: FieldError[] = [];

    if (values.contentReference.trim().length === 0) {
      errors.push("contentReference");
    }
    if (values.description.trim().length === 0) {
      errors.push("description");
    }
    if (!contactOptional && values.reporterName.trim().length === 0) {
      errors.push("reporterName");
    }
    // An address that was typed has to be usable even when it was optional -
    // otherwise the reasoned answer of Art. 16 (5) never arrives.
    const email = values.reporterEmail.trim();
    if (contactOptional) {
      if (email.length > 0 && !EMAIL_PATTERN.test(email)) {
        errors.push("reporterEmail");
      }
    } else if (!EMAIL_PATTERN.test(email)) {
      errors.push("reporterEmail");
    }
    if (!values.goodFaithConfirmed) {
      errors.push("goodFaithConfirmed");
    }

    return errors;
  };

  const hasError = (field: FieldError) => fieldErrors.indexOf(field) >= 0;

  const submit = async (event: FormEvent) => {
    event.preventDefault();

    const errors = collectErrors();
    setFieldErrors(errors);
    if (errors.length > 0) {
      return;
    }

    setBusy(true);
    setFailure(null);

    const result = await submitDsaReport(values, language);

    setBusy(false);

    if (!result.ok) {
      setFailure(result);
      return;
    }

    setReceipt(result.data);
  };

  const contactEmail = (contact && contact.contactEmail) || CONTACT_EMAIL;
  const imprintUrl = (contact && contact.imprintUrl) || IMPRINT_URL;
  const languages =
    contact && contact.languages && contact.languages.length > 0
      ? contact.languages
          .map((code) => t(`dsa.language.${code}`, { defaultValue: code }))
          .join(", ")
      : null;

  const submitTheObjection = async (event: FormEvent) => {
    event.preventDefault();

    const errors: string[] = [];
    if (objection.caseId.trim().length === 0) {
      errors.push("caseId");
    }
    if (!EMAIL_PATTERN.test(objection.email.trim())) {
      errors.push("email");
    }

    setObjectionErrors(errors);
    if (errors.length > 0) {
      return;
    }

    setObjectionBusy(true);
    setObjectionFailure(null);

    const result = await submitObjection(objection);

    setObjectionBusy(false);

    if (!result.ok) {
      setObjectionFailure(result);
      return;
    }

    setObjectionDone(true);
  };

  /** The point of contact and what happens next. Shown in every state. */
  const contactSections = (
    <>
      <section className="pub-panel" aria-label={t("dsa.contact.title")}>
        <h2 className="pub-panel-title">{t("dsa.contact.title")}</h2>
        <p className="pub-text">{t("dsa.contact.intro")}</p>
        <p className="pub-text">
          <a href={`mailto:${contactEmail}`} className="pub-link">
            {contactEmail}
          </a>
        </p>
        {languages ? (
          <p className="pub-text">
            {t("dsa.contact.languages", { languages })}
          </p>
        ) : null}
        <p className="pub-text">{t("dsa.contact.noAccount")}</p>
        <p className="pub-text">
          {t("dsa.contact.provider")}{" "}
          <a
            href={imprintUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="pub-link"
          >
            {t("dsa.contact.providerLink")}
          </a>
        </p>
      </section>

      <section className="pub-panel" aria-label={t("dsa.process.title")}>
        <h2 className="pub-panel-title">{t("dsa.process.title")}</h2>
        <ul className="pub-list">
          <li>{t("dsa.process.acknowledge")}</li>
          <li>{t("dsa.process.decide")}</li>
          <li>{t("dsa.process.informReporter")}</li>
          <li>{t("dsa.process.informAffected")}</li>
          <li>{t("dsa.process.objection")}</li>
          <li>{t("dsa.process.courts")}</li>
        </ul>
        <p className="pub-text">
          <a
            href={TERMS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="pub-link"
          >
            {t("dsa.process.termsLink")}
          </a>
        </p>
      </section>

      {/*
        Art. 17 (3) (f) DSA: the statement of reasons has to name a way to object,
        reachable by someone whose account was just suspended. The case id plus the
        address it went to is the whole gate - no login here either.
      */}
      <section className="pub-panel" aria-label={t("dsa.objection.title")}>
        <h2 className="pub-panel-title">{t("dsa.objection.title")}</h2>
        <p className="pub-text">{t("dsa.objection.intro")}</p>

        {objectionDone ? (
          <div className="pub-notice pub-notice-success" role="status">
            <p>{t("dsa.objection.done")}</p>
          </div>
        ) : (
          <form onSubmit={submitTheObjection} noValidate>
            {objectionFailure ? (
              <div className="pub-notice pub-notice-error" role="alert">
                <p>
                  {objectionFailure.status === 404
                    ? t("dsa.objection.notFound")
                    : t(`dsa.failure.${objectionFailure.reason}`)}
                </p>
                <p>
                  {t("dsa.failure.fallback")}{" "}
                  <a href={`mailto:${contactEmail}`} className="pub-link">
                    {contactEmail}
                  </a>
                </p>
              </div>
            ) : null}

            <div className="pub-field">
              <label className="pub-label" htmlFor="dsa-case">
                {t("dsa.objection.fields.caseId")}
              </label>
              <input
                id="dsa-case"
                className={`pub-input${
                  objectionErrors.indexOf("caseId") >= 0
                    ? " pub-input-invalid"
                    : ""
                }`}
                type="text"
                value={objection.caseId}
                onChange={(event) =>
                  setObjection({ ...objection, caseId: event.target.value })
                }
              />
              <span className="pub-hint">
                {t("dsa.objection.hints.caseId")}
              </span>
              {objectionErrors.indexOf("caseId") >= 0 ? (
                <span className="pub-field-error">
                  {t("dsa.objection.errors.caseId")}
                </span>
              ) : null}
            </div>

            <div className="pub-field">
              <label className="pub-label" htmlFor="dsa-objection-email">
                {t("dsa.objection.fields.email")}
              </label>
              <input
                id="dsa-objection-email"
                className={`pub-input${
                  objectionErrors.indexOf("email") >= 0
                    ? " pub-input-invalid"
                    : ""
                }`}
                type="email"
                autoComplete="email"
                value={objection.email}
                onChange={(event) =>
                  setObjection({ ...objection, email: event.target.value })
                }
              />
              <span className="pub-hint">{t("dsa.objection.hints.email")}</span>
              {objectionErrors.indexOf("email") >= 0 ? (
                <span className="pub-field-error">
                  {t("dsa.objection.errors.email")}
                </span>
              ) : null}
            </div>

            <div className="pub-field">
              <label className="pub-label" htmlFor="dsa-objection-statement">
                {t("dsa.objection.fields.statement")}{" "}
                <span className="pub-optional">{t("dsa.optional")}</span>
              </label>
              <textarea
                id="dsa-objection-statement"
                className="pub-textarea"
                maxLength={4000}
                value={objection.statement}
                onChange={(event) =>
                  setObjection({ ...objection, statement: event.target.value })
                }
              />
            </div>

            <button
              type="submit"
              className="pub-secondary-btn"
              disabled={objectionBusy}
            >
              {objectionBusy
                ? t("dsa.objection.submitting")
                : t("dsa.objection.submit")}
            </button>
          </form>
        )}
      </section>
    </>
  );

  if (receipt) {
    return (
      <div className="pub-page">
        <button
          type="button"
          className="pub-back-link"
          onClick={() => navigate(withEntryQuery("/"))}
        >
          {t("app.backHome")}
        </button>

        <h1 className="pub-title">{t("dsa.done.title")}</h1>

        <div className="pub-notice pub-notice-success" role="status">
          <p>
            {t("dsa.done.received", { date: formatDate(receipt.receivedAt, locale) })}
          </p>
          <p>{t("dsa.done.reference", { reference: receipt.reportId })}</p>
        </div>

        <section className="pub-panel" aria-label={t("dsa.done.title")}>
          <p className="pub-text">
            {values.reporterEmail.trim()
              ? t("dsa.done.answer", { email: values.reporterEmail.trim() })
              : t("dsa.done.noAnswer")}
          </p>
        </section>

        {contactSections}
      </div>
    );
  }

  return (
    <div className="pub-page">
      <button
        type="button"
        className="pub-back-link"
        onClick={() => navigate(withEntryQuery("/"))}
      >
        {t("app.backHome")}
      </button>

      <h1 className="pub-title">{t("dsa.title")}</h1>
      <p className="pub-lead">{t("dsa.lead")}</p>

      {failure ? (
        <div className="pub-notice pub-notice-error" role="alert">
          {/*
            The controller answers with a code, not a sentence; the wording belongs
            here and an unknown code falls back to the HTTP status reason.
          */}
          <p>
            {failure.body && failure.body.code
              ? t(`dsa.problem.${failure.body.code}`, {
                  defaultValue: t(`dsa.failure.${failure.reason}`),
                })
              : t(`dsa.failure.${failure.reason}`)}
          </p>
          <p>
            {t("dsa.failure.fallback")}{" "}
            <a href={`mailto:${contactEmail}`} className="pub-link">
              {contactEmail}
            </a>
          </p>
        </div>
      ) : null}

      <form onSubmit={submit} noValidate>
        <section className="pub-panel" aria-label={t("dsa.form.title")}>
          <h2 className="pub-panel-title">{t("dsa.form.title")}</h2>

          <div className="pub-field">
            <label className="pub-label" htmlFor="dsa-category">
              {t("dsa.fields.category")}
            </label>
            <select
              id="dsa-category"
              className="pub-select"
              value={values.category}
              onChange={(event) =>
                setValue("category", event.target.value as DsaCategory)
              }
            >
              {DSA_CATEGORIES.map((category) => (
                <option key={category} value={category}>
                  {t(`dsa.category.${category}`)}
                </option>
              ))}
            </select>
          </div>

          {contactOptional ? (
            <div className="pub-notice pub-notice-info">
              <p>{t("dsa.form.anonymousAllowed")}</p>
            </div>
          ) : null}

          <div className="pub-field">
            <label className="pub-label" htmlFor="dsa-location">
              {t("dsa.fields.contentReference")}
            </label>
            <textarea
              id="dsa-location"
              className={`pub-textarea${
                hasError("contentReference") ? " pub-input-invalid" : ""
              }`}
              maxLength={MAX_CONTENT_REFERENCE}
              value={values.contentReference}
              onChange={(event) =>
                setValue("contentReference", event.target.value)
              }
            />
            <span className="pub-hint">
              {t("dsa.hints.contentReference")}
            </span>
            {hasError("contentReference") ? (
              <span className="pub-field-error">
                {t("dsa.errors.contentReference")}
              </span>
            ) : null}
          </div>

          <div className="pub-field">
            <label className="pub-label" htmlFor="dsa-description">
              {t("dsa.fields.description")}
            </label>
            <textarea
              id="dsa-description"
              className={`pub-textarea${
                hasError("description") ? " pub-input-invalid" : ""
              }`}
              maxLength={MAX_DESCRIPTION}
              value={values.description}
              onChange={(event) => setValue("description", event.target.value)}
            />
            <span className="pub-hint">{t("dsa.hints.description")}</span>
            {hasError("description") ? (
              <span className="pub-field-error">
                {t("dsa.errors.description")}
              </span>
            ) : null}
          </div>
        </section>

        <section className="pub-panel" aria-label={t("dsa.reporter.title")}>
          <h2 className="pub-panel-title">{t("dsa.reporter.title")}</h2>
          <p className="pub-text">
            {contactOptional
              ? t("dsa.reporter.optionalIntro")
              : t("dsa.reporter.intro")}
          </p>

          <div className="pub-field">
            <label className="pub-label" htmlFor="dsa-name">
              {t("dsa.fields.reporterName")}{" "}
              {contactOptional ? (
                <span className="pub-optional">{t("dsa.optional")}</span>
              ) : null}
            </label>
            <input
              id="dsa-name"
              className={`pub-input${
                hasError("reporterName") ? " pub-input-invalid" : ""
              }`}
              type="text"
              autoComplete="name"
              maxLength={MAX_REPORTER_NAME}
              value={values.reporterName}
              onChange={(event) => setValue("reporterName", event.target.value)}
            />
            {hasError("reporterName") ? (
              <span className="pub-field-error">
                {t("dsa.errors.reporterName")}
              </span>
            ) : null}
          </div>

          <div className="pub-field">
            <label className="pub-label" htmlFor="dsa-email">
              {t("dsa.fields.reporterEmail")}{" "}
              {contactOptional ? (
                <span className="pub-optional">{t("dsa.optional")}</span>
              ) : null}
            </label>
            <input
              id="dsa-email"
              className={`pub-input${
                hasError("reporterEmail") ? " pub-input-invalid" : ""
              }`}
              type="email"
              autoComplete="email"
              maxLength={MAX_REPORTER_EMAIL}
              value={values.reporterEmail}
              onChange={(event) => setValue("reporterEmail", event.target.value)}
            />
            <span className="pub-hint">{t("dsa.hints.reporterEmail")}</span>
            {hasError("reporterEmail") ? (
              <span className="pub-field-error">
                {t("dsa.errors.reporterEmail")}
              </span>
            ) : null}
          </div>

          {/*
            Art. 16 (2) (c) DSA - without this declaration the submission is not a
            notice in the sense of the article.
          */}
          <div className="pub-field">
            <label className="pub-choice">
              <input
                type="checkbox"
                checked={values.goodFaithConfirmed}
                onChange={(event) =>
                  setValue("goodFaithConfirmed", event.target.checked)
                }
              />
              <span>{t("dsa.fields.goodFaith")}</span>
            </label>
            {hasError("goodFaithConfirmed") ? (
              <span className="pub-field-error">
                {t("dsa.errors.goodFaith")}
              </span>
            ) : null}
          </div>

          <button type="submit" className="pub-primary-btn" disabled={busy}>
            {busy ? t("dsa.form.submitting") : t("dsa.form.submit")}
          </button>
          {/*
            Art. 13 GDPR: a reporter has no account here, so the information is
            owed the moment he types his name, address and accusation. The sentence
            alone is not that information; the link is.
          */}
          <p className="pub-submit-hint">
            {t("dsa.form.privacyHint")}{" "}
            <a
              href={PRIVACY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="pub-link"
            >
              {t("dsa.form.privacyLink")}
            </a>
          </p>
        </section>
      </form>

      {contactSections}
    </div>
  );
}

export default DsaReportPage;
